import { httpsCallable } from 'firebase/functions';
import { FormEvent, useCallback, useMemo, useState } from 'react';
import GlobalSidebar from '../components/GlobalSidebar';
import GlobalEmptyState from '../components/GlobalEmptyState';
import { GlobalButton } from '../components/GlobalButton';
import { GlobalInput } from '../components/GlobalInput';
import { ScenarioLeversList } from '../components/ScenarioLeversList';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { useNetWorthScenarios } from '../hooks/useNetWorthScenarios';
import { functions } from '../lib/firebase';
import authStyles from '../pages/auth/authPages.module.css';
import type { NetWorthScenario } from '../types';
import { formatDateLong, formatEuro } from '../utils/formatters';
import { computeNetWorthSnapshotsByDomain } from '../utils/netWorthSnapshot';
import styles from './ScenarioBuilderView.module.css';

const DEFAULT_HORIZON_YEARS = 10;
const MIN_HORIZON_YEARS = 1;
const MAX_HORIZON_YEARS = 30;
// Keep this strictly LONGER than the backend function's timeoutSeconds (240s)
// so the server's own structured error surfaces instead of a client deadline.
// The SDK default (70s) is far shorter than the reasoning model's response time.
const SCENARIO_CALLABLE_TIMEOUT_MS = 270_000;

function parseTargetNetWorth(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
}

export function buildScenarioChatPrompt(scenario: NetWorthScenario): string {
  const gapLine =
    scenario.projectedNetWorth >= scenario.targetNetWorth
      ? 'Gap: target met or exceeded at horizon'
      : `Gap: ${formatEuro(scenario.targetNetWorth - scenario.projectedNetWorth, true)} remaining to close`;

  const lines = [
    'I built a net worth scenario in Perfina and want to discuss how to reach my goal.',
    '',
    `Target: ${formatEuro(scenario.targetNetWorth, true)} in ${scenario.horizonYears} ${
      scenario.horizonYears === 1 ? 'year' : 'years'
    }`,
    `Baseline today: ${formatEuro(scenario.baselineNetWorth, true)}`,
    `Projected at horizon: ${formatEuro(scenario.projectedNetWorth, true)}`,
    gapLine,
    '',
    `Summary: ${scenario.summary}`,
  ];

  if (scenario.assumptions.length > 0) {
    lines.push('', 'Assumptions:');
    scenario.assumptions.forEach((assumption) => {
      lines.push(`- ${assumption}`);
    });
  }

  if (scenario.levers.length > 0) {
    lines.push('', 'Suggested levers:');
    scenario.levers.forEach((lever) => {
      lines.push(`- ${lever.title}: ${lever.description}`);
    });
  }

  lines.push('', 'Which levers should I prioritize, and what risks should I watch for?');

  return lines.join('\n');
}

function buildScenarioErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/invalid-argument') {
      return 'Enter a positive target amount and a horizon between 1 and 30 years.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to build a scenario.';
    }
    if (code === 'functions/deadline-exceeded') {
      return 'Scenario build timed out. Try a shorter horizon or try again in a moment.';
    }
    if (code === 'functions/unavailable' || code === 'functions/internal') {
      return 'Scenario build failed. Try again in a moment.';
    }
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase') && message !== 'deadline-exceeded') {
      return message;
    }
  }
  return err instanceof Error ? err.message : 'Scenario build failed.';
}

async function buildNetWorthScenario(
  targetNetWorth: number,
  horizonYears: number
): Promise<NetWorthScenario> {
  const fn = httpsCallable<
    { targetNetWorth: number; horizonYears: number },
    NetWorthScenario
  >(functions, 'buildNetWorthScenario', { timeout: SCENARIO_CALLABLE_TIMEOUT_MS });
  const result = await fn({ targetNetWorth, horizonYears });
  return result.data;
}

function ScenarioResultsPanel({
  scenario,
  readOnly,
  saving,
  onSave,
  onDiscussInChat,
  discussInChatLoading,
}: {
  scenario: NetWorthScenario;
  readOnly: boolean;
  saving: boolean;
  onSave?: () => void;
  onDiscussInChat?: (prompt: string) => void;
  discussInChatLoading?: boolean;
}) {
  return (
    <div className={styles.results} aria-label={readOnly ? 'Saved scenario' : 'Scenario results'}>
      {readOnly ? (
        <div className={`${authStyles.banner} ${authStyles.bannerSuccess}`} role="status">
          Saved scenario from {formatDateLong(scenario.createdAt)} — read-only
        </div>
      ) : null}

      {!readOnly && onSave ? (
        <div className={styles.actions} style={{ gap: 8 }}>
          <GlobalButton type="button" variant="secondary" isLoading={saving} disabled={saving} onClick={onSave}>
            Save scenario
          </GlobalButton>
          {onDiscussInChat ? (
            <GlobalButton
              type="button"
              variant="secondary"
              isLoading={discussInChatLoading}
              disabled={discussInChatLoading || saving}
              onClick={() => onDiscussInChat(buildScenarioChatPrompt(scenario))}
            >
              Discuss in chat
            </GlobalButton>
          ) : null}
        </div>
      ) : onDiscussInChat ? (
        <div className={styles.actions} style={{ gap: 8 }}>
          <GlobalButton
            type="button"
            variant="secondary"
            isLoading={discussInChatLoading}
            disabled={discussInChatLoading}
            onClick={() => onDiscussInChat(buildScenarioChatPrompt(scenario))}
          >
            Discuss in chat
          </GlobalButton>
        </div>
      ) : null}

      <section className={styles.resultsSection} aria-labelledby="scenario-summary-heading">
        <h2 id="scenario-summary-heading" className={styles.resultsHeading}>
          Summary
        </h2>
        <p className={styles.summary}>{scenario.summary}</p>
      </section>

      <section className={styles.resultsSection} aria-labelledby="scenario-comparison-heading">
        <h2 id="scenario-comparison-heading" className={styles.resultsHeading}>
          Projected vs target
        </h2>
        <div className={styles.comparisonGrid}>
          <div className={styles.comparisonCard}>
            <div className={styles.comparisonLabel}>Target</div>
            <div className={styles.comparisonValue}>{formatEuro(scenario.targetNetWorth, true)}</div>
            <p className={`${styles.comparisonHint} muted`}>
              in {scenario.horizonYears} {scenario.horizonYears === 1 ? 'year' : 'years'}
            </p>
          </div>
          <div className={`${styles.comparisonCard} ${styles.comparisonCardEmphasis}`}>
            <div className={styles.comparisonLabel}>Projected</div>
            <div className={styles.comparisonValue}>{formatEuro(scenario.projectedNetWorth, true)}</div>
            <p className={`${styles.comparisonHint} muted`}>
              from {formatEuro(scenario.baselineNetWorth, true)} baseline
            </p>
          </div>
          <div className={styles.comparisonCard}>
            <div className={styles.comparisonLabel}>Gap</div>
            <div
              className={`${styles.comparisonValue} ${
                scenario.projectedNetWorth >= scenario.targetNetWorth
                  ? styles.comparisonValueMet
                  : styles.comparisonValueGap
              }`}
            >
              {scenario.projectedNetWorth >= scenario.targetNetWorth
                ? 'Target met'
                : formatEuro(scenario.targetNetWorth - scenario.projectedNetWorth, true)}
            </div>
            <p className={`${styles.comparisonHint} muted`}>
              {scenario.projectedNetWorth >= scenario.targetNetWorth
                ? 'Projected net worth reaches or exceeds target'
                : 'Remaining to close by horizon'}
            </p>
          </div>
        </div>
      </section>

      {scenario.assumptions.length > 0 ? (
        <section className={styles.resultsSection} aria-labelledby="scenario-assumptions-heading">
          <h2 id="scenario-assumptions-heading" className={styles.resultsHeading}>
            Assumptions
          </h2>
          <ul className={styles.assumptionsList}>
            {scenario.assumptions.map((assumption) => (
              <li key={assumption}>{assumption}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className={styles.resultsSection} aria-labelledby="scenario-levers-heading">
        <h2 id="scenario-levers-heading" className={styles.resultsHeading}>
          Levers
        </h2>
        <ScenarioLeversList levers={scenario.levers} />
      </section>
    </div>
  );
}

export function ScenarioBuilderView() {
  const { user } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { financialItems, companies, bankAccounts, realEstateItems } = useAppData();
  const {
    scenarios: savedScenarios,
    loading: historyLoading,
    error: historyError,
    saveScenario,
  } = useNetWorthScenarios(user?.uid ?? '');
  const [targetInput, setTargetInput] = useState('');
  const [horizonYears, setHorizonYears] = useState(DEFAULT_HORIZON_YEARS);
  const [building, setBuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [buildSuccess, setBuildSuccess] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [targetError, setTargetError] = useState<string | null>(null);
  const [scenario, setScenario] = useState<NetWorthScenario | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [builderSidebarOpen, setBuilderSidebarOpen] = useState(false);

  const baselineNetWorth = useMemo(() => {
    const snapshots = computeNetWorthSnapshotsByDomain({
      financialItems,
      companies,
      bankAccounts: bankAccounts ?? [],
      realEstateItems: realEstateItems ?? [],
    });
    return snapshots.combined.netWorth;
  }, [financialItems, companies, bankAccounts, realEstateItems]);

  const openBuilderSidebar = useCallback(() => {
    setScenario(null);
    setReadOnly(false);
    setSelectedScenarioId(null);
    setBuildError(null);
    setBuildSuccess(null);
    setSaveSuccess(null);
    setSaveError(null);
    setTargetError(null);
    setBuilderSidebarOpen(true);
  }, []);

  const closeBuilderSidebar = useCallback(() => {
    if (building) return;
    setBuilderSidebarOpen(false);
    setBuildError(null);
    setBuildSuccess(null);
    setTargetError(null);
  }, [building]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setBuildError(null);
      setBuildSuccess(null);
      setSaveSuccess(null);
      setSaveError(null);
      setTargetError(null);
      setScenario(null);
      setReadOnly(false);
      setSelectedScenarioId(null);

      const targetNetWorth = parseTargetNetWorth(targetInput);
      if (targetNetWorth == null) {
        setTargetError('Enter a positive target amount in euros.');
        return;
      }

      setBuilding(true);
      try {
        const builtScenario = await buildNetWorthScenario(targetNetWorth, horizonYears);
        setScenario(builtScenario);
        setBuildSuccess('Scenario built successfully.');
        setBuilderSidebarOpen(false);
      } catch (err) {
        setBuildError(buildScenarioErrorMessage(err));
      } finally {
        setBuilding(false);
      }
    },
    [targetInput, horizonYears]
  );

  const handleSaveScenario = useCallback(async () => {
    if (!scenario || readOnly) return;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      await saveScenario(scenario);
      setSaveSuccess('Scenario saved to your history.');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save scenario.');
    } finally {
      setSaving(false);
    }
  }, [scenario, readOnly, saveScenario]);

  const handleSelectSavedScenario = useCallback((saved: NetWorthScenario) => {
    setScenario(saved);
    setReadOnly(true);
    setSelectedScenarioId(saved.id);
    setBuildError(null);
    setBuildSuccess(null);
    setSaveSuccess(null);
    setSaveError(null);
    setTargetError(null);
  }, []);

  const plusIcon = (
    <img
      src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const toolbarActions = (
    <GlobalButton
      type="button"
      variant="secondary"
      disabled={building || !user?.uid}
      onClick={openBuilderSidebar}
      icon={plusIcon}
    >
      Build scenario
    </GlobalButton>
  );

  const showEmptyState =
    Boolean(user?.uid) &&
    !historyLoading &&
    !historyError &&
    savedScenarios.length === 0 &&
    !scenario;

  const showScenarioList = savedScenarios.length > 0;
  const showContentShell = showScenarioList || Boolean(scenario);

  return (
    <ViewPage title="Scenarios" actions={toolbarActions}>
      <ViewSectionHeader
        eyebrow="Goal planning"
        title="Scenarios"
        subtitle="Set a target net worth and time horizon — Perfina suggests levers across your registry, bank cash, and portfolio."
      />

      {user?.uid && historyLoading ? (
        <p className="muted" style={{ margin: 0 }}>
          Loading saved scenarios…
        </p>
      ) : null}

      {historyError ? (
        <div className={`${authStyles.banner} ${authStyles.bannerError}`} role="alert">
          {historyError}
        </div>
      ) : null}

      {showEmptyState ? (
        <GlobalEmptyState
          buttonText="Build scenario"
          onButtonClick={openBuilderSidebar}
          buttonIcon={plusIcon}
        />
      ) : null}

      {showContentShell ? (
      <section className={styles.shell} aria-label="Saved scenarios">
        {showScenarioList ? (
              <ul className={styles.scenarioList}>
                {savedScenarios.map((saved) => {
                  const isSelected = selectedScenarioId === saved.id;
                  return (
                    <li key={saved.id}>
                      <button
                        type="button"
                        className={`${styles.comparisonCard} ${
                          isSelected ? styles.comparisonCardEmphasis : ''
                        } ${styles.scenarioListItem}`}
                        aria-current={isSelected ? 'true' : undefined}
                        onClick={() => handleSelectSavedScenario(saved)}
                      >
                        <div className={styles.comparisonLabel}>{formatDateLong(saved.createdAt)}</div>
                        <div className={styles.comparisonValue}>
                          {formatEuro(saved.targetNetWorth, true)}
                        </div>
                        <p className={`${styles.comparisonHint} muted`}>
                          {saved.horizonYears} {saved.horizonYears === 1 ? 'year' : 'years'} horizon
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
        ) : null}

        {scenario ? (
          <>
            {saveSuccess ? (
              <div className={`${authStyles.banner} ${authStyles.bannerSuccess}`} role="status">
                {saveSuccess}
              </div>
            ) : null}
            {saveError ? (
              <div className={`${authStyles.banner} ${authStyles.bannerError}`} role="alert">
                {saveError}
              </div>
            ) : null}
            <ScenarioResultsPanel
              scenario={scenario}
              readOnly={readOnly}
              saving={saving}
              onSave={readOnly ? undefined : handleSaveScenario}
            />
          </>
        ) : null}
      </section>
      ) : null}

      <GlobalSidebar isOpen={builderSidebarOpen} onClose={closeBuilderSidebar}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Build scenario</h2>
          <p className="bankProviderConnectIntro">
            Set a target net worth and time horizon. Perfina will suggest levers across your
            registry, bank cash, and portfolio.
          </p>

          <form className={styles.form} onSubmit={handleSubmit}>
            <GlobalInput
              label="Baseline net worth"
              type="text"
              value={formatEuro(baselineNetWorth, true)}
              readOnly
              disabled
              aria-readonly="true"
            />

            <GlobalInput
              label="Target net worth (€)"
              type="text"
              inputMode="decimal"
              value={targetInput}
              onChange={(event) => {
                setTargetInput(event.target.value);
                if (targetError) setTargetError(null);
                if (buildSuccess) setBuildSuccess(null);
                if (saveSuccess) setSaveSuccess(null);
              }}
              error={targetError ?? undefined}
              disabled={building}
            />

            <div className={styles.horizonField}>
              <label className={styles.horizonLabel} htmlFor="scenario-horizon-years">
                <span>Horizon</span>
                <span className={styles.horizonValue}>
                  {horizonYears} {horizonYears === 1 ? 'year' : 'years'}
                </span>
              </label>
              <input
                id="scenario-horizon-years"
                type="range"
                className={styles.horizonRange}
                min={MIN_HORIZON_YEARS}
                max={MAX_HORIZON_YEARS}
                step={1}
                value={horizonYears}
                disabled={building || !user?.uid}
                onChange={(event) => {
                  setHorizonYears(Number(event.target.value));
                  if (buildSuccess) setBuildSuccess(null);
                  if (saveSuccess) setSaveSuccess(null);
                }}
              />
              <p className={`${styles.horizonHint} muted`}>1–30 years</p>
            </div>

            {buildError ? <p className="sidebarFormError">{buildError}</p> : null}

            <div className="sidebarFormActions">
              <GlobalButton
                type="button"
                variant="secondary"
                disabled={building}
                onClick={closeBuilderSidebar}
              >
                Cancel
              </GlobalButton>
              <GlobalButton
                type="submit"
                variant="primary"
                isLoading={building}
                disabled={building || !user?.uid}
              >
                Build scenario
              </GlobalButton>
            </div>
          </form>
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
